import { useEffect, useRef, useState } from "react";
import { PhoneFrame } from "../components/PhoneFrame";
import { SectionHeading } from "../components/SectionHeading";
import { SCREENS } from "../data/screens";
import { prefersReducedMotion } from "../hooks/useReveal";
import { RichText, useSiteTranslation } from "../i18n";
import "../styles/journey.css";

// Step text comes from home.journey.steps in the site translations, in this order.
const STEP_SCREENS = [SCREENS.compatibility, SCREENS.chats, SCREENS.privacy, SCREENS.profile];

const SWAP_DELAY_MS = 160;

/** Tracks the selected step and delays the visible swap so the screenshot can fade. */
function useStepSwap() {
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState(0);
  const timer = useRef();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const select = (index) => {
    if (index === active) return;
    setActive(index);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShown(index), prefersReducedMotion() ? 0 : SWAP_DELAY_MS);
  };

  return { active, shown, changing: active !== shown, select };
}

export function JourneySection() {
  const { active, shown, changing, select } = useStepSwap();
  const { t } = useSiteTranslation();
  const steps = t("home.journey.steps", { returnObjects: true });
  const step = steps[shown];
  const screen = STEP_SCREENS[shown];

  return (
    <section className="section journey" id="journey">
      <div className="wrap">
        <SectionHeading
          eyebrow={t("home.journey.eyebrow")}
          title={<RichText k="home.journey.title" />}
          lead={t("home.journey.lead")}
        />

        <div className="journey-layout">
          <div className="journey-tabs" role="tablist" aria-label={t("home.journey.tabsLabel")}>
            {steps.map(({ title, summary }, index) => (
              <button
                key={title}
                className={`journey-tab${index === active ? " active" : ""}`}
                type="button"
                role="tab"
                aria-selected={index === active}
                onClick={() => select(index)}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <b>{title}</b>
                  <small>{summary}</small>
                </div>
                <i />
              </button>
            ))}
          </div>

          <div className="journey-visual" data-reveal>
            <div className="journey-orbit" aria-hidden="true" />
            <PhoneFrame
              className="journey-phone"
              imageClassName={changing ? "changing" : undefined}
              src={screen.src}
              alt={t(screen.alt)}
            />
            <div className="journey-caption">
              <span>{step.label}</span>
              <p>{step.copy}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
